import axios from 'axios';
import { debounce } from 'lodash';
import { message } from 'antd';
import * as utils from '../../../utils/Utils';
import { getApiRoot, getNonGwApiRoot } from '../../../constants/ParamountReactConstants';
import {
  createNewUpload,
  updateUploadProgress,
  uploadFailed,
  uploadFinished,
} from '../../parent/language/update';
import { AsyncDispatch } from '../../../../types/global';
import { getFileExtension } from '../../../utils/ImportExportUtils';
import { EXCEL_FILE_EXTENSION } from '../../../constants/ImportExportConstants';
import { ExportTypes, ImportTypes } from '../../../../types/import_export';
import { fetchPresignedUrl } from '../../importer';

const CancelToken = axios.CancelToken;

let fetchDynamicFieldListsCancelToken: any;

export const fetchDynamicFieldLists = () => {
  if (fetchDynamicFieldListsCancelToken) fetchDynamicFieldListsCancelToken();

  return {
    type: 'FETCH_DYNAMIC_FIELD_LISTS',
    payload: axios.get(`${getNonGwApiRoot()}/accounts/dynamic_custom_field_lists`, {
      cancelToken: new CancelToken(c => {
        fetchDynamicFieldListsCancelToken = c;
      }),
    }),
  };
};

export const fetchUsedDynamicValueResources = () => {
  return {
    type: 'FETCH_USED_DYNAMIC_VALUE_RESOURCES',
    payload: axios.get(`${getNonGwApiRoot()}/accounts/used_dynamic_value_resources`),
  };
};

export const createDynamicFieldList = (listName: string, type: 'string' | 'numeric') => {
  return {
    type: 'CREATE_DYNAMIC_FIELD_LIST',
    payload: axios.post(`${getNonGwApiRoot()}/accounts/dynamic_custom_field_lists`, {
      name: listName,
      value_data_type: type,
    }),
  };
};

export function deleteDynamicFieldList(id: number) {
  const params = utils.createURL([{ name: 'id', values: id }]);

  return {
    type: 'DELETE_DYNAMIC_FIELD_LIST',
    meta: { id },
    payload: axios.delete(`${getNonGwApiRoot()}/accounts/dynamic_custom_field_lists${params}`),
  };
}

export function importDynamicFieldListExcelFile(file: any, listName: string) {
  return (dispatch: any) => {
    const extension = getFileExtension(file.name);
    if (extension !== EXCEL_FILE_EXTENSION) {
      message.error('invalid file');
    } else {
      dispatch(createDynamicFieldListExcelImport(file, file.uid, listName));
    }
  };
}

function createDynamicFieldListExcelImport(file: any, uuid: string, listName: string) {
  return async (dispatch: AsyncDispatch) => {
    dispatch(createNewUpload(uuid, file.name, ImportTypes.EXCEL));

    const urlResponse: any = await dispatch(fetchPresignedUrl(file.name));
    const bucket = urlResponse.value.data;
    const url = bucket.url;
    const filename = bucket.filename;

    await axios.put(url, file, {
      headers: { 'Content-Type': file.type },
      // Send upload progress for file upload.
      onUploadProgress: debounce(progress => {
        dispatch(updateUploadProgress(uuid, progress));
      }, 100),
    });

    try {
      const params = utils.createURL([
        { name: 'file_name', values: filename },
        { name: 'list_name', values: listName },
      ]);
      const response: any = await axios.post(
        `${getApiRoot()}/imports/dynamic_custom_field_lists${params}`
      );
      dispatch(uploadFinished(uuid, response.data.import_log_id, true));
    } catch (e) {
      dispatch(uploadFailed(uuid));
    }
  };
}

export function exportDynamicFieldListExcelFile(listName: string) {
  return async (dispatch: AsyncDispatch) => {
    let exportRes;
    try {
      const params = utils.createURL([{ name: 'list_name', values: listName }]);
      exportRes = await axios.post(`${getApiRoot()}/exports/dynamic_custom_field_lists${params}`);

      dispatch({
        type: '@EXPORTER/START_EXPORT_FULFILLED',
        meta: {
          fileType: ExportTypes.EXCEL,
          brandId: 0,
        },
        payload: exportRes,
      });
    } catch (e: any) {
      message.error(e.response.data.message);
    }
  };
}

// Row CRUD operations
export function searchDynamicFieldListRow(listName: string, key: string) {
  const params = utils.createURL([
    { name: 'list_name', values: listName },
    { name: 'key', values: key },
  ]);

  return axios.get(`${getNonGwApiRoot()}/accounts/dynamic_custom_field_lists/rows${params}`);
}

export function createDynamicFieldListRow(listName: string, key: string, value: string) {
  const params = utils.createURL([
    { name: 'list_name', values: listName },
    { name: 'key', values: key },
    { name: 'value', values: value },
  ]);

  return axios.post(`${getNonGwApiRoot()}/accounts/dynamic_custom_field_lists/rows${params}`);
}

export function updateDynamicFieldListRow(listName: string, key: string, value: string) {
  const params = utils.createURL([
    { name: 'list_name', values: listName },
    { name: 'key', values: key },
    { name: 'value', values: value },
  ]);

  return axios.patch(`${getNonGwApiRoot()}/accounts/dynamic_custom_field_lists/rows${params}`);
}

export function deleteDynamicFieldListRow(listName: string, key: string) {
  const params = utils.createURL([
    { name: 'list_name', values: listName },
    { name: 'key', values: key },
  ]);

  return axios.delete(`${getNonGwApiRoot()}/accounts/dynamic_custom_field_lists/rows${params}`);
}
